<div class="p-2">
  <!-- Header Section -->
  <div class="flex flex-wrap justify-between gap-2 items-center mb-6">
    <div>
      <h1 class="text-2xl font-bold text-gray-800">User Management</h1>
      
      <p class="text-gray-600">Manage users across the system.</p>
      
      @if (selectedSubscriptionName) {
        <p class="text-sm font-medium text-gray-700 mt-2">{{ selectedSubscriptionName }}</p>
      }
    </div>
    <div class="flex gap-3">
      <!-- <p-button 
        label="Export" 
        icon="pi pi-download" 
        severity="secondary"
        (onClick)="exportUsers()"
        [disabled]="loading">
      </p-button> -->
      @if (hasCreateUserPermission()) {
      <p-button 
        label="Add New User" 
        icon="pi pi-plus" 
        (onClick)="addNewUser()" 
        [disabled]="loading"
  aria-label="Add new user to the system" i18n-aria-label>
      </p-button>
      }
    </div>
  </div>

  <!-- Pending Approvals Alert - Hide from FSR users -->
  @if (pendingApprovals.length > 0 && !isFSR()) {
  <div class="mb-6">
    <div class="bg-yellow-50 border border-yellow-300 rounded-xl p-6 flex items-center justify-between shadow-sm">
      <div class="flex items-center gap-4">
        <div class="bg-yellow-100 rounded-full p-3">
          <i class="pi pi-user" style="font-size: 1.5rem; color: #ff9f1c"></i>
        </div>
        <div>
          <h3 class="font-semibold text-yellow-800 text-lg">New Users for Approval</h3>
          <p class="text-yellow-700">{{ pendingApprovals.length }} users are awaiting your review and approval.</p>
        </div>
      </div>
      @if (hasPendingViewPermission()) {
      <p-button appGlobalLoadingDisable 
        label="Review Now" 
        severity="secondary" 
        [outlined]="true" 
        severity="warn" 
        (onClick)="filterPendingUsers()"
  aria-label="Review pending user applications" i18n-aria-label>
      </p-button>
      }
    </div>
  </div>
  }
  <!-- Search and Filters -->
  <p-card styleClass="mb-6">
    <div class="flex flex-col lg:flex-row gap-4">
      <!-- Search Input -->
      <div class="flex-1">
        <span class="">
          <p-iconfield iconPosition="left">
            <p-inputicon>
              <i class="pi pi-search"></i>
            </p-inputicon>
            <input pInputText type="text" placeholder="Search users by name, email..." i18n-placeholder [(ngModel)]="searchText" (keyup.enter)="applyFilters()" class="min-w-[200px]" fluid>
          </p-iconfield>

        </span>
      </div>

      <!-- Filters -->
      <div class="flex flex-wrap gap-3">

  <p-select [options]="roleOptions" [(ngModel)]="selectedRole" placeholder="Role: All" i18n-placeholder optionLabel="label" optionValue="value" styleClass="min-w-[150px]" [disabled]="loadingRoles">
        </p-select>
        


  <p-select [options]="statusOptions" [(ngModel)]="selectedStatus" placeholder="Status: All" i18n-placeholder optionLabel="label" optionValue="value" styleClass="min-w-[150px]">
        </p-select>

        <p-selectbutton [(ngModel)]="selectedArchived" [options]="getArchivedOptions()" (onChange)="onArchivedChange($event)" optionLabel="label" optionValue="value">
        </p-selectbutton>
      </div>

      <!-- Action Buttons -->
      <div class="flex gap-2">
        <p-button 
          label="Search" 
          icon="pi pi-search" 
          (onClick)="applyFilters()" 
          [disabled]="loading"
          [attr.aria-busy]="loading"
          aria-label="Search and filter users" i18n-aria-label>
        </p-button>
        <p-button 
          label="Reset" 
          icon="pi pi-refresh" 
          severity="secondary" 
          [outlined]="true" 
          (onClick)="resetFilters()" 
          [disabled]="loading"
          aria-label="Reset all filters to default values" i18n-aria-label>
        </p-button>
      </div>
    </div>
  </p-card>

  <!-- Users Table -->
  <p-card class="!p-3">
    <div class="overflow-x-auto min-h-screen">
      <p-table [value]="users" [loading]="loading" [paginator]="false" [rows]="pageSize" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries" [tableStyle]="{'min-width': '1000px', 'width': '100%'}" styleClass="p-datatable-sm">

        <!-- User Column -->
        <ng-template pTemplate="header">
          <tr>
            <th class="min-w-[200px]">USER</th>
            <th class="min-w-[120px]">ROLE</th>
            <th class="min-w-[140px]">INSURANCE NO.</th>
            <th class="min-w-[140px]">CONTACT NO.</th>
            <th class="min-w-[100px]">STATUS</th>
            <th 
              class="min-w-[120px] cursor-pointer hover:bg-gray-50 transition-colors duration-200" 
              (click)="toggleSort()"
              (keydown.enter)="toggleSort()"
              (keydown.space)="toggleSort(); $event.preventDefault()"
              tabindex="0"
              role="button"
              [attr.aria-label]="'Sort by created date, currently ' + (sortOrder === -1 ? 'descending' : 'ascending')">
              <div class="flex items-center justify-between">
                <span>CREATED</span>
                <i class="pi" [ngClass]="sortOrder === -1 ? 'pi-sort-down' : 'pi-sort-up'" aria-hidden="true"></i>
              </div>
            </th>
            <th class="min-w-[180px]">ACTIONS</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-user>
          <tr [ngClass]="{'!bg-yellow-50': user.status === 'PENDING'}">
            <!-- User Info -->
            <td class="min-w-[200px]">
              <div class="flex items-center gap-3">
                <p-avatar [label]="getInitials(user.firstName, user.lastName)" size="normal" shape="circle">
                </p-avatar>
                <div>
                  <div class="font-semibold">{{ user.firstName }} {{ user.lastName }}</div>
                  <div class="text-sm text-gray-500">
                    <a 
                      href="mailto:{{ user.email }}" 
                      class="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
                      [attr.aria-label]="'Send email to ' + user.firstName + ' ' + user.lastName">
                      {{ user.email }}
                    </a>
                  </div>
                  @if (user.refNo) {
                  <div class="text-xs text-gray-400 mt-1">{{ user.refNo }}</div>
                  }
                </div>
              </div>
            </td>

            <!-- Role -->
            <td class="min-w-[120px]">
              <span class="font-medium">{{ user.roleId?.name || 'N/A' }}</span>
            </td>

            <!-- Insurance Number -->
            <td class="min-w-[140px]">
              <span class="text-gray-600">{{ user.nationalInsuranceNo || 'N/A' }}</span>
            </td>

            <!-- Contact Number -->
            <td class="min-w-[140px]">
              @if (user.mobileNumber) {
              <a 
                href="tel:{{ user.mobileNumber }}" 
                class="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
                [attr.aria-label]="'Call ' + user.firstName + ' ' + user.lastName">
                {{ user.mobileNumber }}
              </a>
              } @else {
              <span class="text-gray-600">N/A</span>
              }
            </td>

            <!-- Status -->
            <td class="min-w-[100px]">
              <p-tag [value]="getStatusLabel(user.status)" [severity]="getStatusBadgeSeverity(user.status)">
              </p-tag>
            </td>

            <!-- Created Date -->
            <td class="min-w-[120px]">
              <span class="text-gray-600">{{ formatDate(user.createdAt) }}</span>
            </td>

            <!-- Actions -->
            <td class="min-w-[180px]">
              <div class="flex items-center gap-2">
                <!-- Pending Approval Actions - Only show if user can review this user -->
                @if (user.status === 'PENDING' && canReviewUser(user)) {
                <div>
                  <p-button appGlobalLoadingDisable 
                    label="Review Application" 
                    size="small" 
                    severity="warn" 
                    (onClick)="openUserReview(user._id)"
                    [attr.aria-label]="'Review application for ' + user.firstName + ' ' + user.lastName">
                  </p-button>
                </div>
                }
                <!-- View User button - Only show if user can review this user -->
                @if (!(user.status === 'PENDING') && canReviewUser(user)) {
                <div>
                  <p-button appGlobalLoadingDisable 
                    label="View User" 
                    size="small" 
                    [outlined]="true" 
                    (onClick)="openUserReview(user._id)"
                    [attr.aria-label]="'View details for ' + user.firstName + ' ' + user.lastName">
                  </p-button>
                </div>
                }
                <!-- Regular Actions - Only show if user can review this user -->
                @if (user.status !== 'PENDING' && canReviewUser(user)) {
                <div class="relative">
                  <p-button appGlobalLoadingDisable 
                    icon="pi pi-ellipsis-v" 
                    size="small" 
                    severity="secondary" 
                    [text]="true" 
                    (onClick)="showActionMenu($event, user)" 
                    [attr.aria-label]="'More actions for ' + user.firstName + ' ' + user.lastName"
                    [attr.aria-haspopup]="true"
                    [attr.aria-expanded]="false"
                    pTooltip="Actions">
                  </p-button>
                </div>
                }
                <!-- Show restricted message for FSR users -->
                @if (isFSR()) {
                <div class="text-sm text-gray-500 italic">
                  No actions available
                </div>
                }
              </div>
            </td>
          </tr>
        </ng-template>

        <!-- Empty State -->
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="7" class="text-center py-8">
              <div class="flex flex-col items-center gap-2" role="status">
                <i class="pi pi-users text-4xl text-gray-300" aria-hidden="true"></i>
                <p class="text-gray-500">No users found</p>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>

      <!-- Pagination -->
      <nav class="flex flex-wrap justify-between gap-2 mt-4" aria-label="User list pagination">
        <div class="text-sm text-gray-600" role="status" aria-live="polite">
          Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalUsers) }} of {{ totalUsers }} entries
        </div>

        <p-paginator 
          [rows]="pageSize" 
          [totalRecords]="totalUsers" 
          [rowsPerPageOptions]="pageSizes" 
          (onPageChange)="onPageChange($event)" 
          [showCurrentPageReport]="false"
          [attr.aria-label]="'Navigate through user pages'">
        </p-paginator>
      </nav>
    </div>
  </p-card>

  <!-- Action Menu (outside table for better positioning) -->
  <p-menu 
    #menu 
    [popup]="true" 
    [model]="actionMenuItems" 
    appendTo="body"
    role="menu"
    [attr.aria-label]="'User actions menu'">
  </p-menu>

  

  <!-- User Review Drawer -->
  <p-drawer 
    [(visible)]="isDrawerVisible" 
    position="right" 
    [modal]="true" 
    styleClass="!w-full md:!w-[30rem] lg:!w-[60rem]"
    role="dialog"
    [attr.aria-label]="'User review panel'"
    [attr.aria-modal]="true">

    <ng-template pTemplate="headless">
      <div class="flex flex-col h-full">
        <!-- Header -->
        <header class="flex items-center justify-between px-6 py-2 shrink-0 border-b border-gray-200">
          <h2 class="text-lg font-semibold" id="user-review-heading">User Review</h2>
          <p-button appGlobalLoadingDisable 
            icon="pi pi-times" 
            [text]="true" 
            [rounded]="true" 
            (onClick)="closeUserReview()"
            aria-label="Close user review panel">
          </p-button>
        </header>

        <!-- Content -->
        <main class="flex-1 overflow-y-auto p-6" aria-labelledby="user-review-heading">
          <!-- Loading State -->
          @if (userReviewLoading) {
          <div class="flex flex-col items-center justify-center py-8" role="status" aria-live="polite">
            <i class="pi pi-spin pi-spinner text-4xl text-blue-500 mb-4" aria-hidden="true"></i>
            <p class="text-gray-600">Loading user details...</p>
          </div>
          }

          <!-- User Details -->
          @if (!userReviewLoading && selectedUserForReview) {
          <div class="space-y-6">
            <!-- Personal Information -->
            <div class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Personal Information</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <p class="text-gray-900">{{ selectedUserForReview.title || 'N/A' }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <p class="text-gray-900">{{ selectedUserForReview.firstName }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <p class="text-gray-900">{{ selectedUserForReview.lastName }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <p class="text-gray-900">{{ selectedUserForReview.email }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p class="text-gray-900">
                    <a 
                      href="mailto:{{ selectedUserForReview.email }}" 
                      class="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
                      [attr.aria-label]="'Send email to ' + selectedUserForReview.email">
                      {{ selectedUserForReview.email }}
                    </a>
                  </p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                  <p class="text-gray-900">
                    @if (selectedUserForReview.mobileNumber) {
                    <a 
                      href="tel:{{ selectedUserForReview.mobileNumber }}" 
                      class="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
                      [attr.aria-label]="'Call ' + selectedUserForReview.mobileNumber">
                      {{ selectedUserForReview.mobileNumber }}
                    </a>
                    } @else {
                    N/A
                    }
                  </p>
                </div>
              </div>
            </div>

            <!-- Residential Address -->
            <div class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Residential Address</h3>
              <div class="bg-gray-50 rounded-lg p-4">
                <div class="grid grid-cols-1 gap-2">
                  @if (selectedUserForReview.address?.houseNumber) {
                  <div>
                    <p class="text-gray-900">{{ selectedUserForReview.address?.houseNumber }}</p>
                  </div>
                  }
                  @if (selectedUserForReview.address?.streetAddress) {
                  <div>
                    <p class="text-gray-900">{{ selectedUserForReview.address?.streetAddress }}</p>
                  </div>
                  }
                  @if (selectedUserForReview.address?.city) {
                  <div>
                    <p class="text-gray-900">{{ selectedUserForReview.address?.city }}</p>
                  </div>
                  }
                  @if (selectedUserForReview.address?.country) {
                  <div>
                    <p class="text-gray-900">{{ selectedUserForReview.address?.country }}</p>
                  </div>
                  }
                  @if (selectedUserForReview.address?.postcode) {
                  <div>
                    <p class="text-gray-900">Postal Code: {{ selectedUserForReview.address?.postcode }}</p>
                  </div>
                  }
                </div>
              </div>
            </div>

            <!-- Identification and Business -->
            <div class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Identification and Business</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Citizenship</label>
                  <p class="text-gray-900">{{ selectedUserForReview.citizenship || 'N/A' }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">National Insurance Number</label>
                  <p class="text-gray-900">{{ selectedUserForReview.nationalInsuranceNo || 'N/A' }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Tax Number</label>
                  <p class="text-gray-900">{{ selectedUserForReview.taxNumber || 'N/A' }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Trade Licence</label>
                  <p class="text-gray-900">{{ selectedUserForReview.tradeLicence || 'N/A' }}</p>
                </div>
              </div>
            </div>

            <!-- ID Card -->
            <div class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">ID Card</h3>
              <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div class="flex items-center gap-3">
                  <i class="pi pi-file-pdf text-red-500 text-xl"></i>
                  <span class="text-gray-900">{{ selectedUserForReview.idCard || 'No ID card uploaded' }}</span>
                </div>
                @if (selectedUserForReview.idCard) {
                <div class="flex items-center gap-2">
                  <p-button appGlobalLoadingDisable icon="pi pi-download" [text]="true" [rounded]="true" (onClick)="downloadIdCard(selectedUserForReview.idCard)">
                  </p-button>
                </div>
                }
              </div>
            </div>

            <!-- Additional Information -->
            <div class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Additional Information</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                  <p class="text-gray-900">{{ selectedUserForReview.refNo || 'N/A' }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <p class="text-gray-900">{{ selectedUserForReview.roleId?.name || 'N/A' }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Subscriber</label>
                  <p class="text-gray-900">{{ selectedUserForReview.subscriberId?.companyName || 'N/A' }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <p-tag [value]="getStatusLabel(selectedUserForReview.status)" [severity]="getStatusBadgeSeverity(selectedUserForReview.status)">
                  </p-tag>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Created Date</label>
                  <p class="text-gray-900">{{ formatDate(selectedUserForReview.createdAt) }}</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                  <p class="text-gray-900">{{ selectedUserForReview.gender || 'N/A' }}</p>
                </div>
              </div>
            </div>

            <!-- Products Section -->
            <div *ngIf="getUserProducts().length > 0" class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Assigned Products</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div *ngFor="let product of getUserProducts()" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                  <i class="pi pi-box text-blue-500"></i>
                  <div>
                    <div class="font-medium text-gray-900">{{ product.name }}</div>
                    <div class="text-sm text-gray-500">{{ product.code }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Territories Section -->
            <div *ngIf="getUserTerritoryRegions().length > 0" class="space-y-4">
              <h3 class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Assigned Territories</h3>
              <p-accordion [value]="0" class="w-full">
                <p-accordion-panel 
                  *ngFor="let region of getUserTerritoryRegions(); let i = index" 
                  [value]="i"
                >
                  <p-accordion-header>{{ region }}</p-accordion-header>
                  <p-accordion-content>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4">
                      <div 
                        *ngFor="let territory of getUserTerritoriesForRegion(region)" 
                        class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border"
                      >
                        <i class="pi pi-map-marker text-green-500"></i>
                        <span class="text-gray-900 font-medium">{{ territory.name }}</span>
                      </div>
                    </div>
                  </p-accordion-content>
                </p-accordion-panel>
              </p-accordion>
            </div>

            <!-- Action Buttons - Only show if user can review this user -->
            @if (selectedUserForReview.status === 'PENDING' && canReviewUser(selectedUserForReview)) {
            
              <div class="flex flex-row gap-3 pt-6 border-t">
              @if (hasApprovePermission()) {
              <p-button 
                [label]="approvingUser ? 'Approving...' : 'Approve User'" 
                [icon]="approvingUser ? 'pi pi-spin pi-spinner' : 'pi pi-check'" 
                (onClick)="approveUser(selectedUserForReview._id)" 
                class=""
                [disabled]="approvingUser || denyingUser"
                [attr.aria-label]="'Approve user ' + selectedUserForReview.firstName + ' ' + selectedUserForReview.lastName">
              </p-button>
              }

              @if (hasDenyPermission()) {
              <p-button 
                [label]="denyingUser ? 'Denying...' : 'Deny User'" 
                [icon]="denyingUser ? 'pi pi-spin pi-spinner' : 'pi pi-times'" 
                [outlined]="true" 
                (onClick)="denyUser(selectedUserForReview._id)" 
                class=""
                severity="danger"
                [disabled]="approvingUser || denyingUser"
                [attr.aria-label]="'Deny user ' + selectedUserForReview.firstName + ' ' + selectedUserForReview.lastName">
              </p-button>
              }
            </div>
         
            }
          </div>
          }
        </main>
      </div>
    </ng-template>
  </p-drawer>

  <!-- Confirmation Dialogs -->
  <p-confirmDialog></p-confirmDialog>

  <!-- Toast Messages -->
  <p-toast></p-toast>